import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { CompanyCredit } from "@/types";
import { useGetCompanyCreditHistory } from "@/services/Company";
import dayjs from "dayjs";

import DataTable from "@/components/molecules/DataTable";
import NonSortableHeader from "@/components/molecules/NonSortableHeader";
import NumberFormatter from "@/components/atoms/NumberFormatter";

interface CreditHistoryTableProps {
  companyId: number;
  currency: string;
}

export default function CreditHistoryTable({ companyId, currency }: CreditHistoryTableProps) {
  const { t } = useTranslation();

  const { data: creditHistory, isFetching } = useGetCompanyCreditHistory(companyId);

  const columns: ColumnDef<CompanyCredit>[] = useMemo(
    () => [
      {
        header: ({ column }) => (
          <NonSortableHeader column={column} label={t("commonAdmin:credit.requestedChangeDate")} />
        ),
        id: "requestedChangeDate",
        cell: ({ row }) =>
          row.original.requestedChangeDate ? dayjs(row.original.requestedChangeDate).format("DD.MM.YYYY") : "-",
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("commonAdmin:credit.approvedDate")} />,
        id: "approvedDate",
        cell: ({ row }) => (row.original.approvedDate ? dayjs(row.original.approvedDate).format("DD.MM.YYYY") : "-"),
      },
      {
        header: ({ column }) => <NonSortableHeader column={column} label={t("common:creditLimit")} />,
        id: "creditLimit",
        cell: ({ row }) => <NumberFormatter value={row.original.creditLimit} suffix={` ${currency}`} />,
      },
    ],
    [t, currency]
  );

  return (
    <DataTable
      onRowClick={undefined}
      data={creditHistory ?? []}
      columns={columns}
      isLoading={isFetching}
      mobileHeaderCell={0}
      showPagination={false}
    />
  );
}
